/**
 * The legacy `restaurants` DB stores some names + addresses in ALL CAPS
 * ("WSKY BAR & GRILL BAR", "101 MONTELAGO BLVD") and others in mixed
 * case ("Giuseppe's Grill", "3300 S Las Vegas BLVD"). Display logic:
 *
 *   1. If a string is "shouting" (>=80% letters uppercase), title-case it.
 *   2. Always normalize known tokens:
 *        - Street-suffix abbreviations get lower-cased (BLVD → Blvd).
 *        - True abbreviations stay uppercase (LV, NV, USA, II...).
 *   3. Runtime overrides (DB-sourced) win over built-in maps and are
 *      filtered by scopeFilter ('name' | 'address').
 *
 * Step 2 runs even when step 1 didn't fire, so mixed-case strings like
 * "3300 S Las Vegas BLVD" still pick up the BLVD → Blvd cleanup.
 *
 * Callers that pass no overrides/scopeFilter get identical behaviour to
 * the previous single-argument signature.
 */

export type OverrideScope = 'both' | 'name' | 'address';

export interface CaseOverride {
  /** The uppercase form to match (case-insensitive). */
  token: string;
  display: string;
  scope: OverrideScope;
}

/** Street/unit suffixes — uppercase forms in legacy data → Title case. */
const BUILTIN_SUFFIX_MAP: Record<string, string> = {
  ST: 'St', AVE: 'Ave', BLVD: 'Blvd', RD: 'Rd', LN: 'Ln', CT: 'Ct', PL: 'Pl',
  DR: 'Dr', WAY: 'Way', HWY: 'Hwy', PKWY: 'Pkwy', CIR: 'Cir', TER: 'Ter',
  TRL: 'Trl', STE: 'Ste', APT: 'Apt', RM: 'Rm', FL: 'Fl', BLDG: 'Bldg',
};

/** True abbreviations / Roman numerals — preserve uppercase even after title-case. */
const BUILTIN_KEEP_UPPER = new Set([
  'LV',   // Las Vegas
  'NV',   // Nevada
  'USA',
  'US',
  'BBQ',
  'HQ',
  'HVAC',
  'II',
  'III',
  'IV',
  'VI',
  'VII',
  'VIII',
  'IX',
]);

function applicableOverrides(
  overrides: ReadonlyArray<CaseOverride>,
  scopeFilter: 'name' | 'address',
): Map<string, string> {
  const map = new Map<string, string>();
  for (const o of overrides) {
    if (o.scope !== 'both' && o.scope !== scopeFilter) continue;
    map.set(o.token.toUpperCase(), o.display);
  }
  return map;
}

function normalizeTokens(s: string, runtime: Map<string, string>): string {
  return s.replace(/\b[A-Za-z]+\b/g, (word) => {
    const upper = word.toUpperCase();
    if (runtime.has(upper)) return runtime.get(upper)!;
    if (BUILTIN_KEEP_UPPER.has(upper)) return upper;
    if (BUILTIN_SUFFIX_MAP[upper] !== undefined) return BUILTIN_SUFFIX_MAP[upper];
    return word;
  });
}

export function toDisplayCase(
  input: string,
  overrides: ReadonlyArray<CaseOverride> = [],
  scopeFilter: 'name' | 'address' = 'name',
): string {
  if (!input) return input;

  const letters = input.replace(/[^A-Za-z]/g, '');
  if (letters.length === 0) return input;

  const upperCount = (letters.match(/[A-Z]/g) ?? []).length;

  let s = input;
  const ratio = upperCount / letters.length;
  if (ratio >= 0.8 || upperCount === 0) {
    // "Shouting" (>=80% uppercase) or fully lowercase — normalise to title case.
    s = s
      .toLowerCase()
      .replace(/(^|[\s\-/])(\p{L})/gu, (_m, sep, ch) => sep + ch.toUpperCase());
  }

  return normalizeTokens(s, applicableOverrides(overrides, scopeFilter));
}
